/**
 * US-504b/c/d — the dossier. Direction B: research is a place you go, not a
 * panel that is always half-open.
 *
 * The résumé beside the job's requirements and screening criteria, at a
 * reading measure, in independently scrolling columns, with a judging rail
 * that carries the SAME draft as the card. `Esc` returns to the exact card.
 *
 * MOUNTING. This component is mounted by `CalibrationJudgingSession` only
 * after the first `R`, and never unmounted afterwards — it is toggled with
 * `visibility`, NOT with `hidden`/`display:none` (review finding H1: a
 * display-less element has no layout box, so every scroll container inside it
 * silently resets to the top). The half of that rule that matters for
 * correctness is "never unmounted, never un-laid-out": a scroll position two
 * thirds down a résumé and a half-typed note must survive the toggle. The other half — not mounting until
 * first asked — is what keeps the ~15-second fast path honestly untouched for
 * the nine candidates in twelve that are never researched.
 *
 * BLINDNESS. Everything on this surface is either the recruiter's own input or
 * the same artifact the model read (`redactedResumeText`) plus the job's own
 * spec. No score, no band, no rank, no similarity value, no highlight derived
 * from the agent's search terms, and — explicitly — no match count and no
 * percentage. A count is a machine verdict wearing a plain shirt, and the
 * divergence the round-end reveal reports is only meaningful if the verdict
 * was formed without any of it.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ArrowLeft, Eye, ExternalLink, AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import CalibrationJudgingControls from './CalibrationJudgingControls';
import type { JudgingDraft, JudgingHandlers, IdentityState } from './calibration-judging';
import type { CalibrationCandidate } from './CalibrationJudgeCard';
import { CALIBRATION_REQUIREMENTS_MAX_CHARS } from '../../types/calibration-api';
import type { CalibrationJobSpecResponse } from '../../types/calibration-api';

type Pane = 'resume' | 'job' | 'judge';

const PANES: readonly { id: Pane; label: string }[] = [
  { id: 'resume', label: 'Résumé' },
  { id: 'job', label: 'Requirements' },
  { id: 'judge', label: 'Judge' },
];

export interface CalibrationJobSpecState {
  data: CalibrationJobSpecResponse | null;
  isLoading: boolean;
  isError: boolean;
  /**
   * REVIEW FINDING H2. Without this the failure was PERMANENT. The query is
   * owned by the page (so it is never unmounted) and configured
   * `staleTime: Infinity` / `gcTime: Infinity` / `refetchOnMount: false` /
   * `refetchOnWindowFocus: false`, so once it errored no code path fetched it
   * again for the life of the round — every remaining candidate was then read
   * against the truncated fallback, and the error copy told the recruiter to
   * close and reopen the dossier, which issued no request at all.
   */
  refetch: () => void;
}

interface CalibrationDossierProps {
  open: boolean;
  candidate: CalibrationCandidate;
  /** "Global Product Manager, Hardware · Round 1 · Candidate 4 of 12". */
  contextLine: string;
  /**
   * The full, uncapped job spec (US-502), fetched lazily on first open. While
   * it is in flight or if it fails, the columns fall back to the polled card
   * copy below — WITH its truncation notice, never to a blank column that
   * would read as "this job records no requirements".
   */
  jobSpec: CalibrationJobSpecState;
  /** The capped copy from the polled `/candidates` payload. */
  fallbackRequirements: string | null;
  fallbackRequirementsTruncated: boolean;
  fallbackScreeningCriteria: string | null;
  draft: JudgingDraft;
  handlers: JudgingHandlers;
  identity: IdentityState;
  isEditing: boolean;
  isLast: boolean;
  onClose: () => void;
  /** Records the judgment and returns to the card, rather than moving on. */
  onSaveAndReturn: () => void;
}

/** Column shell: sticky label, then its own scroll region. */
function Column({
  label,
  active,
  pane,
  children,
  trailing,
}: {
  label: string;
  active: Pane;
  pane: Pane;
  children: React.ReactNode;
  trailing?: React.ReactNode;
}) {
  return (
    <div
      data-pane={pane}
      className={`${active === pane ? 'flex' : 'hidden'} lg:flex min-h-0 min-w-0 flex-col border-stone-200 dark:border-gray-700 lg:border-r lg:last:border-r-0`}
    >
      <div className="flex items-center justify-between gap-2 border-b border-stone-200 dark:border-gray-700 bg-[#fdfcfa] dark:bg-gray-900 px-5 py-2.5 text-[11px] font-bold uppercase tracking-wider text-stone-400 dark:text-gray-500">
        <span>{label}</span>
        {trailing}
      </div>
      <div data-scroll-region className="min-h-0 flex-1 overflow-y-auto px-5 pb-8 pt-4">
        {children}
      </div>
    </div>
  );
}

/**
 * Reading measure. 15px/1.72 at ~62ch is deliberately looser and wider than
 * the card's 13.5px clamp — the card is for scanning, this is for reading, and
 * a reading mode that reuses scanning typography is just the card again.
 */
const READ =
  'max-w-[62ch] whitespace-pre-wrap text-[15px] leading-[1.72] text-stone-900 dark:text-gray-100';

export default function CalibrationDossier({
  open,
  candidate,
  contextLine,
  jobSpec,
  fallbackRequirements,
  fallbackRequirementsTruncated,
  fallbackScreeningCriteria,
  draft,
  handlers,
  identity: identityState,
  isEditing,
  isLast,
  onClose,
  onSaveAndReturn,
}: CalibrationDossierProps) {
  const [pane, setPane] = useState<Pane>('resume');
  const closeRef = useRef<HTMLButtonElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const scrollTops = useRef<Partial<Record<Pane, number>>>({});

  function scrollRegion(target: Pane): HTMLElement | null {
    return gridRef.current?.querySelector<HTMLElement>(`[data-pane="${target}"] [data-scroll-region]`) ?? null;
  }

  /**
   * REVIEW FINDING H1, narrow-viewport half. At the Chrome-extension width the
   * three columns collapse to a tab strip whose inactive panes are toggled with
   * a `hidden` display utility — same mechanism, same defect: switching tabs
   * costs the scroll position of the tab you left. The columns cannot simply be
   * `visibility`-hidden here (they share one grid cell and would stack), so the
   * offset is saved and restored explicitly instead.
   */
  function selectPane(next: Pane) {
    const leaving = scrollRegion(pane);
    if (leaving) scrollTops.current[pane] = leaving.scrollTop;
    setPane(next);
  }

  useLayoutEffect(() => {
    const entering = scrollRegion(pane);
    if (entering) entering.scrollTop = scrollTops.current[pane] ?? 0;
  }, [pane]);

  const { identity, isRevealing, error: identityError, onReveal } = identityState;
  // The reveal is set-once server-side and per-candidate on the client (the
  // session remounts per row), so "sighted" is exactly "an identity exists".
  const sighted = identity !== null;

  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    // Scroll lock: the dossier is a fixed sheet over the page, and a page that
    // scrolls behind it means Esc returns to a different card position than
    // the one that was left — the opposite of "your place is held".
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  const spec = jobSpec.data;
  const requirements = spec ? spec.requirements : fallbackRequirements;
  const screening = spec ? spec.screeningCriteriaText : fallbackScreeningCriteria;
  // Only the fallback copy can be abridged; the job-spec route caps nothing.
  //
  // REVIEW FINDING M1. `isLoading` is part of the condition because without it
  // the in-flight window on a truncated job showed three contradictory things
  // at once: the spinner "Loading the full job spec…", the abridged body, and
  // "the full text could not be loaded". This is the one surface whose entire
  // job is to be scrupulous about what the recruiter is actually reading.
  const settled = !jobSpec.isLoading || !!spec;
  const showTruncationNotice = settled && !spec && fallbackRequirementsTruncated;

  return (
    <div
      data-testid="calibration-dossier"
      /* REVIEW FINDING H1 — see the matching note in CalibrationJudgingSession.
         `hidden` means `display:none`, which has no layout box and therefore
         drops the résumé column's `scrollTop`. `visibility` keeps the box (so
         the scroll position genuinely survives) while still removing the sheet
         from the a11y tree, tab order and hit-testing. */
      style={{ visibility: open ? undefined : 'hidden' }}
      role="dialog"
      aria-modal="true"
      aria-label="Candidate dossier"
      data-sighted={sighted}
      className={`fixed inset-0 z-40 flex flex-col overflow-hidden border-stone-200 bg-[#fdfcfa] shadow-2xl dark:border-gray-700 dark:bg-gray-950 sm:inset-6 sm:rounded-[14px] sm:border ${
        sighted ? 'ring-[3px] ring-amber-400' : ''
      }`}
    >
      <header className="flex flex-wrap items-center gap-3 border-b border-stone-200 bg-stone-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-900 sm:px-5">
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          className="inline-flex items-center gap-2 rounded-[7px] border border-stone-300 bg-white px-3 py-1.5 text-sm font-semibold text-stone-700 hover:bg-stone-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
        >
          <ArrowLeft size={15} aria-hidden="true" />
          Back to judging
          <span
            aria-hidden="true"
            className="rounded border border-b-2 border-stone-300 px-1 text-[10px] font-bold text-stone-500 dark:border-gray-600 dark:text-gray-400"
          >
            Esc
          </span>
        </button>
        <div className="min-w-0">
          <h2 className="text-[15px] font-semibold tracking-tight text-stone-900 dark:text-gray-100">
            Candidate #{candidate.candidateId}
          </h2>
          <p className="truncate text-xs text-stone-500 dark:text-gray-400">{contextLine}</p>
        </div>
        <span className="flex-1" />
        {/* US-504d. The mode tag is the standing answer to "am I still blind?",
            and it changes with the environment rather than being a separate
            thing to notice. */}
        <span
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${
            sighted
              ? 'border-amber-400 bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
              : 'border-stone-200 bg-white text-stone-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400'
          }`}
        >
          <span
            aria-hidden="true"
            className={`block h-1.5 w-1.5 rounded-full ${sighted ? 'bg-amber-400' : 'bg-stone-400 dark:bg-gray-500'}`}
          />
          {sighted ? 'Sighted research' : 'Blind research'}
        </span>
        {!identity ? (
          <button
            type="button"
            onClick={onReveal}
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-[7px] border border-stone-300 bg-white px-2.5 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          >
            <Eye size={14} aria-hidden="true" />
            {isRevealing ? 'Revealing…' : 'Reveal identity'}
          </button>
        ) : (
          <span className="flex flex-wrap items-center gap-3 text-xs">
            <span className="font-semibold text-stone-900 dark:text-gray-100">{identity.candidateName}</span>
            {identity.srProfileUrl && (
              <a
                href={identity.srProfileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-medium text-sky-600 hover:underline dark:text-sky-400"
              >
                Open in SmartRecruiters <ExternalLink size={12} aria-hidden="true" />
              </a>
            )}
            <Link
              to={`/candidates/${identity.candidateId}`}
              className="font-medium text-sky-600 hover:underline dark:text-sky-400"
            >
              View in Recruiter Buddy
            </Link>
          </span>
        )}
      </header>

      {identityError && (
        <p className="border-b border-rose-300 bg-rose-50 px-5 py-2 text-xs text-rose-800 dark:border-rose-800 dark:bg-rose-950/50 dark:text-rose-200">
          {identityError}
        </p>
      )}

      {/* US-504d. Non-dismissing on purpose. There is no control that removes
          it, because "I revealed this person before deciding" is a fact about
          the judgment, not a notification about an event. The back-test reports
          blind and sighted agreement separately, and a banner you can wave away
          is one you can forget. */}
      {sighted && (
        <p className="border-b border-amber-400 bg-amber-100 px-5 py-2.5 text-xs text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
          <strong className="font-semibold">Sighted from here on.</strong> You revealed this
          candidate&rsquo;s identity before recording a verdict. The judgment still counts — it is
          reported in the sighted bucket, not the blind one, so the back-test never confuses taste
          with information the model never had.
        </p>
      )}

      {/* Narrow (Chrome extension, 600–700px): three panes become a tab strip.
          The panes stay MOUNTED and are toggled with a display utility, so
          switching tabs never costs a scroll position either. */}
      <div className="flex border-b border-stone-200 px-4 pt-2 dark:border-gray-700 lg:hidden" role="group" aria-label="Pane">
        {PANES.map((p) => (
          <button
            key={p.id}
            type="button"
            aria-pressed={pane === p.id}
            onClick={() => selectPane(p.id)}
            className={`rounded-t-[7px] border border-b-0 px-3 py-1.5 text-xs font-medium ${
              pane === p.id
                ? 'border-stone-200 bg-sky-50 text-sky-700 dark:border-gray-700 dark:bg-sky-950/50 dark:text-sky-300'
                : 'border-transparent text-stone-500 dark:text-gray-400'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div
        ref={gridRef}
        className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_320px]"
      >
        <Column label="Résumé — redacted, full text" active={pane} pane="resume">
          <div className={READ}>{candidate.redactedResumeText}</div>
        </Column>

        <Column label="What this job requires" active={pane} pane="job">
          {!settled ? (
            <p className="flex items-center gap-2 text-sm text-stone-500 dark:text-gray-400">
              <Loader2 size={14} className="animate-spin" aria-hidden="true" />
              Loading the full job spec…
            </p>
          ) : (
            <>
              {requirements ? (
                <>
                  <h3 className="mb-2 text-[11px] font-bold uppercase tracking-wider text-stone-400 dark:text-gray-500">
                    Requirements
                  </h3>
                  <div className={READ}>{requirements}</div>
                </>
              ) : (
                <p className="text-sm text-stone-500 dark:text-gray-400">
                  {jobSpec.isError
                    ? 'The full job spec could not be loaded, and this job records no requirements in the polled copy either.'
                    : 'This job records no requirements.'}
                </p>
              )}

              {/* REVIEW FINDING H2. The old copy here said "try closing and
                  reopening the dossier" — which issued no request, because the
                  page owns this query and it is configured never to refetch.
                  A remedy that cannot work is worse than no remedy: the
                  recruiter reads the rest of the round against the
                  abridgement believing they fixed it. */}
              {jobSpec.isError && (
                <div className="mt-4 flex flex-wrap items-center gap-3 rounded-r-[7px] border border-l-[3px] border-stone-200 border-l-rose-400 bg-stone-50 px-3 py-2 text-xs text-stone-600 dark:border-gray-700 dark:border-l-rose-500 dark:bg-gray-900 dark:text-gray-300">
                  <span>
                    The full job spec could not be loaded. Anything shown above is the polled copy,
                    which is capped.
                  </span>
                  <button
                    type="button"
                    onClick={jobSpec.refetch}
                    className="inline-flex items-center gap-1.5 rounded-[7px] border border-stone-300 bg-white px-2.5 py-1 font-semibold text-stone-700 hover:bg-stone-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  >
                    <RefreshCw size={12} aria-hidden="true" />
                    Try again
                  </button>
                </div>
              )}
            </>
          )}

          {/* The honest fallback. If the full text is unavailable, the recruiter
              is reading the first 2,000 characters and must be told so — a
              comparison view built on a silent abridgement teaches them to
              trust a job spec that is wrong rather than merely short. */}
          {showTruncationNotice && (
            <p className="mt-4 flex gap-2 rounded-r-[7px] border border-l-[3px] border-stone-200 border-l-amber-400 bg-stone-50 px-3 py-2 text-xs text-stone-600 dark:border-gray-700 dark:border-l-amber-400 dark:bg-gray-900 dark:text-gray-300">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
              <span>
                This is the first {CALIBRATION_REQUIREMENTS_MAX_CHARS.toLocaleString('en-US')}{' '}
                characters of the requirements — the full text could not be loaded, so anything past
                this point is missing.
              </span>
            </p>
          )}

          {screening && (
            <>
              <h3 className="mb-2 mt-7 text-[11px] font-bold uppercase tracking-wider text-stone-400 dark:text-gray-500">
                Screening criteria
              </h3>
              <div className={READ}>{screening}</div>
            </>
          )}

          {/* Deliberately no highlighting, no "9 of 13 met", no pass/fail
              colouring. Every available term source is the model's live
              hypothesis, and naive string matching over it produces a de-facto
              match count — a machine verdict, before the recruiter has given
              theirs. */}
          <p className="mt-6 max-w-[62ch] rounded-r-[7px] border border-l-[3px] border-stone-200 border-l-sky-400 bg-stone-50 px-3 py-2 text-xs text-stone-600 dark:border-gray-700 dark:border-l-sky-500 dark:bg-gray-900 dark:text-gray-300">
            Nothing here is scored, ranked or counted. Calibration exists to capture your judgment
            before you see the machine&rsquo;s.
          </p>
        </Column>

        <div
          data-pane="judge"
          className={`${pane === 'judge' ? 'flex' : 'hidden'} lg:flex min-h-0 min-w-0 flex-col bg-stone-50 dark:bg-gray-900`}
        >
          <div className="border-b border-stone-200 px-4 py-2.5 text-[11px] font-bold uppercase tracking-wider text-stone-400 dark:border-gray-700 dark:text-gray-500">
            Your judgment
          </div>
          <div data-scroll-region className="min-h-0 flex-1 overflow-y-auto p-4">
            <CalibrationJudgingControls
              draft={draft}
              handlers={handlers}
              layout="column"
              prompt="Worth reaching out to?"
              showKeyHints
              noteRows={2}
            />
          </div>
          <div className="grid gap-2 border-t border-stone-200 p-3 dark:border-gray-700">
            <button
              type="button"
              onClick={handlers.onSave}
              aria-disabled={!draft.verdict}
              className={`rounded-[7px] px-4 py-2 text-sm font-semibold text-white ${
                draft.verdict ? 'bg-sky-600 hover:bg-sky-700' : 'cursor-not-allowed bg-stone-300 dark:bg-gray-700'
              }`}
            >
              {isEditing ? 'Save change' : isLast ? 'Save & finish' : 'Save & next candidate'}
            </button>
            <button
              type="button"
              onClick={onSaveAndReturn}
              aria-disabled={!draft.verdict}
              className="rounded-[7px] border border-stone-300 bg-white px-4 py-2 text-sm font-semibold text-stone-700 hover:bg-stone-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700"
            >
              Save &amp; go back to the card
            </button>
            <p className="rounded-[7px] border border-stone-200 bg-white px-3 py-2 text-[11px] text-stone-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
              <strong className="font-semibold text-stone-700 dark:text-gray-200">
                Your place is held.
              </strong>{' '}
              Closing returns you to the exact card, note intact — nothing here is a route change.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
